<template>
  <b-modal v-model="visible" size="xl" centered hide-header hide-footer
           no-close-on-backdrop body-class="p-0" content-class="sbf-content">
    <!-- Hero -->
    <div class="sbf__hero">
      <div class="sbf__hero-icon"><i class="fa-solid fa-list-check"></i></div>
      <div class="sbf__hero-text">
        <h4 class="mb-0">{{ $t('stdBulk.title') }}</h4>
        <small>{{ $t('stdBulk.subtitle') }}</small>
      </div>
      <button type="button" class="sbf__close" @click="close" :disabled="running">
        <feather-icon icon="XIcon" size="18"/>
      </button>
    </div>

    <div class="sbf__body">
      <b-overlay :show="loading || running" rounded opacity="0.4" spinner-variant="primary">
        <div v-if="!loading && !rows.length" class="text-center text-muted py-3">
          <i class="fa-solid fa-circle-check mr-25"></i>{{ $t('stdBulk.noData') }}
        </div>

        <template v-else>
          <div class="sbf__summary">
            <b-badge variant="light-primary">{{ rows.length }} {{ $t('stdBulk.clients') }}</b-badge>
            <b-badge variant="light-success">{{ selectedIds.length }} {{ $t('stdBulk.selected').toLowerCase() }}</b-badge>
          </div>

          <div class="sbf__table-wrap">
            <b-table small hover responsive :items="rows" :fields="fields" show-empty class="sbf-table mb-0">
              <!-- Expand toggle + client -->
              <template #cell(client)="data">
                <b-button size="sm" variant="flat-primary" class="sbf__chev mr-50" @click.stop="data.toggleDetails()">
                  <i :class="data.detailsShowing ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
                </b-button>
                <span class="font-weight-bold">{{ clientName(data.item) }}</span>
                <small v-if="clientEmail(data.item)" class="text-muted d-block ml-3">{{ clientEmail(data.item) }}</small>
              </template>

              <template #cell(ftd)="data">
                <span class="text-success font-weight-bold">{{ convertToCurrency(data.item.ftdTransaction.amount, data.item.ftdTransaction.currency) }}</span>
                <small class="text-muted d-block" v-if="data.item.ftdTransaction.registrationTime">
                  {{ data.item.ftdTransaction.registrationTime | momentDateTime }}
                </small>
              </template>

              <template #cell(candidates)="data">
                {{ data.item.candidates.length }} {{ $t('stdBulk.deposits') }}
              </template>

              <template #cell(selected)="data">
                <b-badge :variant="groupSelectedCount(data.item) ? 'light-success' : 'light-secondary'">
                  {{ groupSelectedCount(data.item) }} / {{ data.item.candidates.length }}
                </b-badge>
              </template>

              <!-- Expanded: FTD context row + candidate rows with checkboxes -->
              <template #row-details="row">
                <div class="sbf-details">
                  <div class="sbf-details__row sbf-details__row--ftd">
                    <span class="sbf-details__flag"><b-badge variant="success">FTD</b-badge></span>
                    <span class="sbf-details__id">#{{ row.item.ftdTransaction.id }}</span>
                    <span class="sbf-details__amount text-success">{{ convertToCurrency(row.item.ftdTransaction.amount, row.item.ftdTransaction.currency) }}</span>
                    <span class="sbf-details__time" v-if="row.item.ftdTransaction.registrationTime">{{ row.item.ftdTransaction.registrationTime | momentDateTime }}</span>
                    <span class="sbf-details__hint text-muted">{{ $t('stdBulk.ftdRow') }}</span>
                  </div>
                  <div v-for="tx in row.item.candidates" :key="tx.id" class="sbf-details__row">
                    <span class="sbf-details__flag">
                      <b-form-checkbox :checked="!!selected[tx.id]" :disabled="running" @change="toggleTx(tx.id, $event)"/>
                    </span>
                    <span class="sbf-details__id">#{{ tx.id }}</span>
                    <span class="sbf-details__amount">{{ convertToCurrency(tx.amount, tx.currency) }}</span>
                    <span class="sbf-details__time" v-if="tx.registrationTime">{{ tx.registrationTime | momentDateTime }}</span>
                    <b-badge v-if="tx.id === row.item.suggestedStdId" variant="light-warning">{{ $t('stdBulk.suggested') }}</b-badge>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
        </template>
      </b-overlay>

      <!-- Footer -->
      <div class="sbf__footer">
        <b-button variant="outline-secondary" :disabled="running" @click="close">
          {{ $t('operations.cancel') }}
        </b-button>
        <b-button variant="warning" :disabled="running || loading || !selectedIds.length" @click="apply">
          <b-spinner v-if="running" small class="mr-25"/>
          <i v-else class="fa-solid fa-list-check mr-25"></i>
          {{ $t('stdBulk.apply', { count: selectedIds.length }) }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'

/**
 * Temporary STD-bulk fixer (MONETARY_STD_BULK permission): previews every
 * client whose FTD exists but whose later real deposits never got the STD
 * flag, grouped per client with the transactions expandable, and bulk-marks
 * the confirmed ones as STD. The suggested STD (earliest deposit after the
 * FTD) comes pre-selected; the FTD row is context only and cannot be picked.
 */
export default {
  name: 'StdBulkFixModal',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  data: () => ({
    visible: false,
    loading: false,
    running: false,
    groups: [],
    clientsById: {},
    selected: {},
  }),
  computed: {
    rows() {
      return this.groups
    },
    fields() {
      return [
        { key: 'client', label: this.$t('stdBulk.client') },
        { key: 'ftd', label: this.$t('stdBulk.ftd') },
        { key: 'candidates', label: this.$t('stdBulk.candidates') },
        { key: 'selected', label: this.$t('stdBulk.selected') },
      ]
    },
    selectedIds() {
      return Object.keys(this.selected).filter(id => this.selected[id])
    },
  },
  methods: {
    ...mapActions('monetary', ['getStdFixPreview', 'applyStdFix']),
    open() {
      this.visible = true
      this.load()
    },
    close() {
      if (this.running) return
      this.visible = false
    },
    load() {
      this.loading = true
      this.groups = []
      this.clientsById = {}
      this.selected = {}
      return this.getStdFixPreview()
        .then(
          data => {
            this.groups = (data && data.groups) || []
            this.clientsById = ((data && data.clients) || []).reduce((map, client) => {
              map[client.id] = client
              return map
            }, {})
            // Pre-select the calculation's suggestion for every client.
            const selected = {}
            this.groups.forEach(group => {
              if (group.suggestedStdId) selected[group.suggestedStdId] = true
            })
            this.selected = selected
          },
          err => this.showServerErrorNotification('fetchDataError', 'monetary', err)
        )
        .finally(() => { this.loading = false })
    },
    clientName(group) {
      const client = this.clientsById[group.clientId]
      if (!client) return `#${group.clientId}`
      return `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email || `#${group.clientId}`
    },
    clientEmail(group) {
      const client = this.clientsById[group.clientId]
      return client ? client.email : null
    },
    groupSelectedCount(group) {
      return group.candidates.filter(tx => this.selected[tx.id]).length
    },
    toggleTx(id, value) {
      this.$set(this.selected, id, !!value)
    },
    async apply() {
      const ids = this.selectedIds
      if (!ids.length || this.running) return
      this.running = true
      try {
        const results = await this.applyStdFix({ ids }) || []
        const ok = results.filter(r => r.success).length
        const fail = results.length - ok
        if (fail) {
          this.showWarningNotification(`${this.$t('stdBulk.applied', { count: ok })} · ${this.$t('stdBulk.failed', { count: fail })}`)
        } else {
          this.showSuccessNotification(this.$t('stdBulk.applied', { count: ok }))
        }
        this.$emit('done')
        await this.load()
      } catch (err) {
        this.showServerErrorNotification('updateDataError', 'monetary', err)
      } finally {
        this.running = false
      }
    },
  },
}
</script>

<style lang="scss">
/* Same visual language as the monetary action modal (mam-*), own prefix. */
.sbf-content {
  border: none;
  border-radius: .8rem;
  overflow: hidden;
}
.sbf__hero {
  display: flex;
  align-items: center;
  gap: .9rem;
  padding: 1.1rem 1.4rem;
  color: #fff;
  background: linear-gradient(120deg, #4a2a6b, #8b5cf6);
}
.sbf__hero-icon {
  width: 44px;
  height: 44px;
  border-radius: .65rem;
  background: rgba(255, 255, 255, .18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.sbf__hero-text { flex: 1; min-width: 0; h4 { color: #fff; font-weight: 700; } small { opacity: .9; } }
.sbf__close {
  border: none;
  background: rgba(255, 255, 255, .15);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s ease, transform .2s ease;
  &:hover { background: rgba(255, 255, 255, .3); transform: rotate(90deg); }
}

.sbf__body { padding: 1.15rem 1.4rem 1.3rem; }
.sbf__summary { display: flex; gap: .5rem; margin-bottom: .75rem; }
.sbf__table-wrap { max-height: 55vh; overflow-y: auto; }
.sbf__chev { padding: .2rem .45rem; }

.sbf-details {
  padding: .35rem .75rem .5rem;
  display: flex;
  flex-direction: column;
  gap: .3rem;
}
.sbf-details__row {
  display: flex;
  align-items: center;
  justify-content: center; /* expanded transaction lines sit centered, not left */
  gap: .9rem;
  font-size: .85rem;

  &--ftd { opacity: .8; }
}
.sbf-details__flag { width: 52px; flex-shrink: 0; }
.sbf-details__id { min-width: 80px; font-weight: 600; }
.sbf-details__amount { min-width: 110px; font-weight: 600; }
.sbf-details__time { min-width: 140px; }
.sbf-details__hint { font-size: .72rem; }

.sbf__footer {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  margin-top: 1.1rem;
}

.dark-layout {
  .sbf-content { background: #283046; color: #d0d2d6; }
}
</style>
